"use client"

import type React from "react"
import { useState } from "react"
import { Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Territory {
  id: number
  name: string
  territories: string[]
  color: string
}

interface AssignTerritoryModalProps {
  isOpen: boolean
  onClose: () => void
  territories: Territory[]
  regionName: string
  onAssign: (territoryId: number) => void
  onAddTerritory: (name: string) => void
}

const AssignTerritoryModal: React.FC<AssignTerritoryModalProps> = ({
  isOpen,
  onClose,
  territories,
  regionName,
  onAssign,
  onAddTerritory,
}) => {
  const [newPersonName, setNewPersonName] = useState("")
  const [isAddingNew, setIsAddingNew] = useState(false)

  if (!isOpen) return null

  const handleAddNewPerson = () => {
    if (newPersonName.trim()) {
      onAddTerritory(newPersonName.trim())
      setNewPersonName("")
      setIsAddingNew(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Assign {regionName}</h2>
          <p className="text-sm text-muted-foreground">Select a person to assign this region to:</p>
        </div>

        <div className="max-h-[300px] overflow-y-auto p-4 space-y-2">
          {territories.map((territory) => (
            <button
              key={territory.id}
              onClick={() => {
                onAssign(territory.id)
                onClose()
              }}
              className="w-full p-3 flex items-center gap-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
            >
              <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: territory.color }} />
              <div className="flex-1 text-left">
                <div className="font-medium">{territory.name}</div>
                <div className="text-sm text-muted-foreground">{territory.territories.length} regions assigned</div>
              </div>
            </button>
          ))}
        </div>

        <div className="p-4 border-t bg-gray-50 flex flex-col gap-2">
          {isAddingNew ? (
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Enter new person's name"
                value={newPersonName}
                onChange={(e) => setNewPersonName(e.target.value)}
                className="flex-grow"
              />
              <Button onClick={handleAddNewPerson} disabled={!newPersonName.trim()}>
                Add
              </Button>
            </div>
          ) : (
            <Button onClick={() => setIsAddingNew(true)} variant="outline" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Person
            </Button>
          )}
          <Button onClick={onClose} variant="outline" className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AssignTerritoryModal
